import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  createRef,
} from 'react';
import { FileOrigin } from 'filepond';
import { downloadData, remove, uploadData } from 'aws-amplify/storage';
import { useLocation } from 'react-router-dom';

const UploadFileContext = createContext();
const filepondRef1 = createRef();
const filepondRef2 = createRef();

const Provider = ({ children }) => {
  const { state } = useLocation();
  const [files, setFiles] = useState({});
  const [originalFiles, setOriginalFiles] = useState({});
  const [uploadError, setUploadError] = useState({});
  const [prefix, setPrefix] = useState({});

  const uploadFile = useCallback((file, progress, prefix, itemId) => {
    try {
      const content = file.file;
      const blobName = content.name;
      return uploadData({
        path: `files/${prefix}/${itemId}/${blobName}`,
        data: content,
        options: {
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              progress?.(true, transferredBytes, totalBytes);
            }
          },
          resumable: true,
        },
      }).result;
    } catch (error) {
      console.error(error);
    }
  }, []);

  const deleteFile = useCallback((file, prefix, itemId) => {
    return remove({
      path: `files/${prefix}/${itemId}/${file}`,
    });
  }, []);

  const deleteChangedFiles = useCallback(
    itemId => {
      return Promise.all(
        Object.keys(originalFiles).map(k =>
          Object.keys(originalFiles[k]).map(name =>
            originalFiles[k]?.[name]
              .filter(oFile => {
                const foundFile = files[k][name].find(
                  f => f.source === oFile?.url,
                );
                return (
                  oFile?.url &&
                  oFile?.url !== '{}' &&
                  !oFile?.url.includes('corvalentstorage001') &&
                  !oFile?.isFromAssetType &&
                  !foundFile
                );
              })
              .map(oFile => {
                return deleteFile(oFile.url, prefix[k][name], itemId[k][0]);
              }),
          ),
        ),
      );
    },
    [deleteFile, files, originalFiles, prefix],
  );

  const processFiles = useCallback(
    async (itemId, ignoreDeleted) => {
      if (!ignoreDeleted) {
        await deleteChangedFiles(itemId);
      }
      return Promise.all(
        Object.keys(files).flatMap(key => {
          return Array.isArray(files[key])
            ? files[key].flatMap((subItem, index) =>
                Object.entries(subItem).flatMap(([name, filesArray]) => {
                  return Promise.all(
                    filesArray
                      .filter(file => typeof file.source !== 'string' || state)
                      .map(file =>
                        uploadFile(
                          file,
                          null,
                          prefix[key][name],
                          itemId[key][index],
                        ),
                      ),
                  );
                }),
              )
            : Object.entries(files[key]).flatMap(([name, filesArray]) =>
                Promise.all(
                  filesArray
                    .filter(file => file.origin === FileOrigin.INPUT || state)
                    .map(file =>
                      uploadFile(file, null, prefix[key][name], itemId[key][0]),
                    ),
                ),
              );
        }),
      );
    },
    [deleteChangedFiles, files, prefix, state, uploadFile],
  );

  const downloadFile = useCallback(
    async (blobName, progress, prefix, itemId) => {
      return downloadData({
        path: blobName.includes('/')
          ? `files/${blobName}`
          : `files/${prefix}/${itemId}/${blobName}`,
        options: {
          onProgress: ({ transferredBytes, totalBytes }) => {
            progress?.(true, transferredBytes, totalBytes);
          },
        },
      }).result;
    },
    [],
  );

  const value = useMemo(
    () => ({
      uploadFile,
      deleteFile,
      downloadFile,
      uploadError,
      setPrefix,
      setUploadError,
      files,
      setFiles,
      filepondRef1,
      filepondRef2,
      processFiles,
      originalFiles,
      setOriginalFiles,
      prefix,
    }),
    [
      deleteFile,
      downloadFile,
      files,
      originalFiles,
      prefix,
      processFiles,
      uploadError,
      uploadFile,
    ],
  );

  return (
    <UploadFileContext.Provider value={value}>
      {children}
    </UploadFileContext.Provider>
  );
};

export const useUploadFileContext = () => useContext(UploadFileContext);

Provider.propTypes = {
  children: PropTypes.element.isRequired,
};

export default Provider;
